import { useEffect, useState } from "react";
import { Button, Icon, JsonViewer, Modal, Spin, Text, type JsonViewerLabels } from "@app/ui";
import { exportPassportVersion } from "../api/dpp.js";
import { ErrorAlert } from "../errors/index.js";
import { useLang, useT } from "../i18n.js";

interface LoadedDump {
  versionNo: number;
  /** Exactly what the endpoint sent, so a download from here matches the table's download byte for byte. */
  text: string;
  value: unknown;
}

export interface PassportJsonModalProps {
  orgId: string;
  passportId: string;
  publicSlug: string;
  /** Version whose JSON-LD dump is shown; null closes the modal. */
  versionNo: number | null;
  onClose: () => void;
}

/**
 * In-panel viewer for a version's official JSON-LD dump (Art. 27(15)). It reads
 * the same export endpoint as the download button — the full authority-tier
 * view — so opening it lands in the audit trail exactly like a download does.
 */
export function PassportJsonModal({
  orgId,
  passportId,
  publicSlug,
  versionNo,
  onClose,
}: PassportJsonModalProps) {
  const t = useT();
  const { lang } = useLang();
  const [dump, setDump] = useState<LoadedDump | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // Closing keeps the last dump on screen so the fade-out doesn't flash a spinner.
    if (versionNo === null) return;
    let cancelled = false;
    setDump(null);
    setError(null);
    exportPassportVersion(orgId, passportId, versionNo, lang === "tr" ? "tr" : "en", "jsonld")
      .then(async (blob) => {
        const text = await blob.text();
        const value: unknown = JSON.parse(text);
        if (!cancelled) setDump({ versionNo, text, value });
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, passportId, versionNo, lang, attempt]);

  const download = () => {
    if (!dump) return;
    const url = URL.createObjectURL(new Blob([dump.text], { type: "application/ld+json" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `passport-${publicSlug}-v${dump.versionNo}.jsonld`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const labels: JsonViewerLabels = {
    search: t("jsonViewer.search"),
    mode: t("jsonViewer.mode"),
    tree: t("jsonViewer.tree"),
    raw: t("jsonViewer.raw"),
    expandAll: t("jsonViewer.expandAll"),
    collapseAll: t("jsonViewer.collapseAll"),
    expand: t("jsonViewer.expand"),
    collapse: t("jsonViewer.collapse"),
    copy: t("jsonViewer.copy"),
    copied: t("jsonViewer.copied"),
    // No params: `{count}` survives for JsonViewer to fill in.
    matches: t("jsonViewer.matches"),
    noMatches: t("jsonViewer.noMatches"),
  };

  const shownVersion = versionNo ?? dump?.versionNo;

  return (
    <Modal
      open={versionNo !== null}
      onClose={onClose}
      width={880}
      title={shownVersion === undefined ? "JSON-LD" : `v${shownVersion} · JSON-LD`}
      footer={
        <>
          <Button onClick={onClose}>{t("common.close")}</Button>
          <Button
            variant="primary"
            disabled={!dump}
            icon={<Icon name="download" size={14} />}
            onClick={download}
          >
            {t("passport.downloadJsonLd")}
          </Button>
        </>
      }
    >
      <Text small secondary className="mb-3 block">
        {t("passport.exportHint")}
      </Text>
      {error != null ? (
        <ErrorAlert error={error} onRetry={() => setAttempt((n) => n + 1)} />
      ) : dump ? (
        <JsonViewer value={dump.value} labels={labels} />
      ) : (
        <div className="flex justify-center py-10">
          <Spin />
        </div>
      )}
    </Modal>
  );
}
